/**
 * Agent 服务
 */

import api from './api';
import {
  AGENTS_CONFIG,
  getAgentsByCategory as getLocalAgentsByCategory,
  searchAgents as searchLocalAgents,
} from '@/config/agents';
import type { Agent, AgentCategory, ModelListResponse } from '@/types';

// ==================== 后端数据类型 ====================

interface BackendAgent {
  id: string;
  name: string;
  display_name?: string;
  displayName?: string;
  description: string;
  category: string;
  icon?: string;
  model?: string;
  api_endpoint?: string;
  apiEndpoint?: string;
  features?: string[];
  capabilities?: string[];
  input_example?: string;
  inputExample?: string;
  output_example?: string;
  outputExample?: string;
  status?: 'active' | 'inactive' | 'deprecated' | 'beta' | 'experimental';
}

interface RecommendedModelResponse {
  model_id: string;
  model_name: string;
  name?: string;
  provider: string;
  reason: string;
}

interface ModelsByTypeResponse {
  text: ModelInfo[];
  vision: ModelInfo[];
  image_generation: ModelInfo[];
  video_generation: ModelInfo[];
}

interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  type: string;
  context_length?: number;
}

function unwrapData<T = any>(response: any, fallback?: T): T {
  return (response?.data ?? response?.result ?? response ?? fallback) as T;
}

const validCategories: AgentCategory[] = [
  'planning',
  'creation',
  'evaluation',
  'analysis',
  'workflow',
  'utility',
  'character',
  'story',
];

function normalizeCategory(category?: string): AgentCategory {
  return validCategories.includes(category as AgentCategory)
    ? (category as AgentCategory)
    : 'utility';
}

function normalizeStatus(status?: BackendAgent['status']): Agent['status'] {
  if (status === 'active' || status === 'beta' || status === 'experimental') {
    return status;
  }
  return status === 'inactive' || status === 'deprecated' ? 'experimental' : 'active';
}

function unwrapAgentList(response: any, key: 'agents' | 'results' = 'agents'): BackendAgent[] {
  const data = unwrapData<any>(response, []);
  const payload = response?.[key] ?? data?.[key] ?? data;
  return Array.isArray(payload) ? payload : [];
}

function findLocalAgent(agentId: string): Agent | null {
  return AGENTS_CONFIG.find((agent) => agent.id === agentId) || null;
}

/**
 * 将后端蛇形命名转换为前端驼峰命名
 */
function mapBackendAgentToAgent(backendAgent: BackendAgent): Agent {
  return {
    id: backendAgent.id,
    name: backendAgent.name || backendAgent.id,
    displayName: backendAgent.display_name || backendAgent.displayName || backendAgent.name || backendAgent.id,
    description: backendAgent.description || '',
    category: normalizeCategory(backendAgent.category),
    icon: backendAgent.icon || 'Bot',
    model: backendAgent.model || 'gpt-5.4',
    apiEndpoint: backendAgent.api_endpoint || backendAgent.apiEndpoint || '/juben/chat',
    features: backendAgent.features || [],
    capabilities: backendAgent.capabilities || [],
    inputExample: backendAgent.input_example || backendAgent.inputExample || '',
    outputExample: backendAgent.output_example || backendAgent.outputExample || '',
    status: normalizeStatus(backendAgent.status),
  };
}

/**
 * 获取所有可用的 Agents（从后端API）
 */
export async function getAgents(): Promise<Agent[]> {
  try {
    // 优先从后端API获取
    const response = await api.get<{ success: boolean; agents?: BackendAgent[]; total?: number }>('/juben/agents/list');
    const agents = unwrapAgentList(response);

    if (response.success !== false && agents.length > 0) {
      return agents.map(mapBackendAgentToAgent);
    }

    return AGENTS_CONFIG;
  } catch {
    return AGENTS_CONFIG;
  }
}

/**
 * 获取 Agent 信息（从后端API）
 */
export async function getAgentInfo(agentId: string): Promise<Agent | null> {
  try {
    // 优先从后端API获取
    const response = await api.get<any>(`/juben/agents/${agentId}`);
    if (response) {
      const data = unwrapData<any>(response);
      const agentData = response.agent || data?.agent || data;
      if (!agentData?.id) {
        return findLocalAgent(agentId);
      }
      return mapBackendAgentToAgent(agentData);
    }
    return findLocalAgent(agentId);
  } catch {
    return findLocalAgent(agentId);
  }
}

/**
 * 获取可用模型列表
 */
export async function getModels(provider: string = 'openai'): Promise<ModelListResponse> {
  try {
    return await api.get<ModelListResponse>('/juben/models', { provider });
  } catch {
    return {
      success: true,
      provider,
      models: [
        {
          name: 'gpt-5.4',
          display_name: 'gpt-5.4',
          description: 'OpenAI-compatible gpt-5.4 degraded fallback model',
          max_tokens: 65536,
        },
      ],
      default_model: 'gpt-5.4',
      purpose_models: {},
      total_count: 1,
    };
  }
}

/**
 * 获取推荐模型
 */
export async function getRecommendedModel(purpose: string): Promise<string> {
  try {
    const response = await api.get<{ success: boolean; recommended_model?: RecommendedModelResponse }>(
      '/juben/models/recommend',
      { purpose }
    );
    return response.recommended_model?.model_id || response.recommended_model?.name || 'gpt-5.4';
  } catch {
    return 'gpt-5.4';
  }
}

/**
 * 按类型获取模型
 */
export async function getModelsByType(): Promise<ModelsByTypeResponse> {
  try {
    const response = await api.get<{ success: boolean; models?: ModelsByTypeResponse }>(
      '/juben/models/types'
    );
    // 后端返回格式: { success: true, models: { text: [], vision: [], ... } }
    return response.models || {
      text: [],
      vision: [],
      image_generation: [],
      video_generation: [],
    };
  } catch {
    return {
      text: [
        {
          id: 'gpt-5.4',
          name: 'gpt-5.4',
          provider: 'openai',
          type: 'text',
          context_length: 65536,
        },
      ],
      vision: [],
      image_generation: [],
      video_generation: [],
    };
  }
}

/**
 * 搜索 Agents（从后端API）
 */
export async function searchAgents(query: string): Promise<Agent[]> {
  try {
    // 从后端API搜索
    const response = await api.get<{ success: boolean; results?: BackendAgent[] }>('/juben/agents/search', { query });
    const results = unwrapAgentList(response, 'results');
    if (response.success !== false && results.length > 0) {
      return results.map(mapBackendAgentToAgent);
    }
    return [];
  } catch {
    return searchLocalAgents(query);
  }
}

/**
 * 按分类获取 Agents（从后端API）
 */
export async function getAgentsByCategory(category: string): Promise<Agent[]> {
  try {
    // 从后端API获取
    const response = await api.get<{ success: boolean; agents?: BackendAgent[] }>('/juben/agents/list', { category });
    const agents = unwrapAgentList(response);
    if (response.success !== false && agents.length > 0) {
      return agents.map(mapBackendAgentToAgent);
    }
    return getLocalAgentsByCategory(normalizeCategory(category));
  } catch {
    return getLocalAgentsByCategory(normalizeCategory(category));
  }
}

/**
 * 获取Agent分类列表（从后端API）
 */
export async function getAgentCategories(): Promise<{
  categories: Record<string, { name: string; icon: string; description: string }>;
  counts: Record<string, number>;
  total: number;
}> {
  try {
    const response = await api.get<any>('/juben/agents/categories');
    const data = unwrapData<any>(response, {});
    return {
      categories: data.categories || response.categories || {},
      counts: data.counts || response.counts || {},
      total: data.total || response.total || 0,
    };
  } catch {
    return {
      categories: {},
      counts: {},
      total: 0,
    };
  }
}
